import type {
  CartItem,
  DraftLineItem,
  FinancialSummary,
  Order,
  OrderLineItem,
} from "../types";

function getLineItemUnitPrice(
  order: Order,
  lineItemId: string,
  { includeTaxes = false }: { includeTaxes?: boolean } = {},
): number {
  const lineItem = order.lineItems?.find(
    (li: OrderLineItem) => li.line_item_id === lineItemId,
  );
  const price = parseFloat((lineItem as any)?.price ?? "0");
  if (isNaN(price)) return 0;

  let taxPerUnit = 0;
  if (includeTaxes) {
    const quantity = (lineItem as any)?.quantity ?? 1;
    const taxLines: { price?: string }[] = (lineItem as any)?.tax_lines ?? [];
    const totalTax = taxLines.reduce(
      (sum, tl) => sum + parseFloat(tl.price ?? "0"),
      0,
    );
    taxPerUnit = quantity > 0 ? totalTax / quantity : 0;
  }

  return (price + taxPerUnit) * 100;
}

export function calculateTotals(
  lineItems: DraftLineItem[],
  order: Order,
  rebuyItems: CartItem[] = [],
): FinancialSummary {
  const currency = (order.currency as any)?.shopCurrency ?? "EUR";

  let refundTotal = 0;
  let exchangeVariantTotal = 0;
  let exchangeCreditTotal = 0;
  let discountTotal = 0;

  for (const item of lineItems) {
    const unitPrice = getLineItemUnitPrice(order, item.lineItemId);
    const itemTotal = unitPrice * item.quantity;

    switch (item.resolution) {
      case "refund":
        refundTotal += itemTotal;
        break;
      case "discount":
        discountTotal += itemTotal;
        break;
      case "exchange":
        exchangeVariantTotal += itemTotal;
        break;
      case "exchange-credit":
        exchangeCreditTotal +=
          getLineItemUnitPrice(order, item.lineItemId, { includeTaxes: true }) *
          item.quantity;
        break;
    }
  }

  const rebuyTotal = rebuyItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const exchangeTotal = exchangeVariantTotal + exchangeCreditTotal;

  const amountDue = Math.max(
    0,
    rebuyTotal +
      exchangeVariantTotal -
      exchangeCreditTotal -
      refundTotal -
      discountTotal,
  );

  return {
    refundTotal,
    exchangeTotal,
    discountTotal,
    rebuyTotal,
    amountDue,
    currency,
  };
}
